import { DashboardTileType, IDashboardDefinition, TTileDefinition } from "@components/dashboard";
import { ifAll, IGetValueArgs, not } from "@components/smart/FieldInfo";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { EntitySetName } from "@odata/GeneratedEntityTypes";
import { ElectronicSubmissionTypeCode, VatPayerRegistrationActionCode } from "@odata/GeneratedEnums";
import { getCompanyCurrency, isCashBasisAccountingCompany, isOnOrganizationLevel } from "@utils/CompanyUtils";
import { fetchUnfinishedDocumentsCounts, getUnfinishedDocumentsTableData } from "@utils/DraftUtils";
import i18next from "i18next";
import { capitalize } from "lodash";

import BusinessCardTile from "../../components/tiles/businessCardTile";
import TodoListTile from "../../components/tiles/todoList/TodoListTile";
import { EMPTY_DASH } from "../../constants";
import { Status } from "../../enums";
import { ROUTE_ELECTRONIC_SUBMISSION } from "../../routes";
import Currency from "../../types/Currency";
import { getUtcDayjs } from "../../types/Date";
import NumberType from "../../types/Number";
import { getSortedDocumentEntityTypeNamesWithPermission } from "../documents/Document.utils";
import { getLastSubmissionByType } from "../electronicSubmission/ElectronicSubmission.utils";
import {
    getVatSubmissionFrequencyFromPeriod,
    getVatSubmissionPeriodName
} from "../electronicSubmission/VatSubmission.utils";
import { currencyScaleFormatter } from "./CustomerDashboardDef";
import {
    agendaIsVatRegistered,
    getAgendaWorkOverviewTileDef,
    getAgendaWorkOverviewTileDefCashBasisAccounting,
    getBankAccountsTileDef,
    getCashBoxTileDef,
    getChatsTileDef, getCreditAccountBalancesTileDef,
    getCustomerSupportTileDef,
    getDemoTenantTileDef,
    getGrantTileDef,
    getIncomeExpenseOverviewTileDef,
    getKeyboardShortcutsTileDef, getLiabilitiesToShareholdersTileDef,
    getManualsDef,
    getNextElectronicSubmissionDate,
    getNotClearedDocumentsDef,
    getPaymentDocumentTileDef, getProfitLossTileDef,
    getPurchaseTileDef,
    getVatAssessmentTurnoverData,
    getVatAssessmentTurnoverLimits,
    getVatOverviewChartTileData,
    getVatTileDef,
    getVideoHelpTileDef,
    getWelcomeTileDef,
    isInCustomerPortal
} from "./Home.utils";

function getVatAssessmentTurnoverSeverity(statusCode: VatPayerRegistrationActionCode): Status {
    switch (statusCode) {
        case VatPayerRegistrationActionCode.NotRequired:
            return Status.Success;
        case VatPayerRegistrationActionCode.Required:
            return Status.Warning;
        case VatPayerRegistrationActionCode.RequiredImmediately:
            return Status.Error;
    }
}

/**
 * Dashboard configuration for accountant with specific selected agenda
 */
export function getDashboardDefinition(): IDashboardDefinition {
    const isCba = (args: IGetValueArgs) => { // TODO: temporary, figure out how this should work on cba company
        return isCashBasisAccountingCompany(args.context);
    };

    const tileDefinition: TTileDefinition = {
        Welcome: getWelcomeTileDef(),
        Purchase: getPurchaseTileDef(),
        IncomeExpenseOverview: {
            ...getIncomeExpenseOverviewTileDef(),
            CPs: UserPermissionCodes.Reports
        },
        ProfitLoss: getProfitLossTileDef(),
        CreditAccountBalances: getCreditAccountBalancesTileDef(),
        LiabilitiesToShareholders: getLiabilitiesToShareholdersTileDef(),
        VatOverview: {
            type: DashboardTileType.Chart,
            title: i18next.t("Home:VatOverviewChart.Heading"),
            size: { w: 4, h: 2 },
            chartData: getVatOverviewChartTileData,
            formatter: currencyScaleFormatter,
            isVisible: ifAll(agendaIsVatRegistered, not(isCba)),
            CPs: UserPermissionCodes.VatStatement
        },
        Vat: {
            ...getVatTileDef(),
            CPs: UserPermissionCodes.VatStatement,
        },
        TodoList: {
            title: i18next.t("Home:TodoList.Heading"),
            type: DashboardTileType.Custom,
            component: TodoListTile,
            size: { w: 2, h: 2 }
        },
        VideoHelp: getVideoHelpTileDef(),
        BusinessCard: {
            title: i18next.t("Home:BusinessCard.Heading"),
            type: DashboardTileType.Custom,
            size: { w: 2, h: 1 },
            component: BusinessCardTile
        },
        VatAssessmentTurnover: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:VatAssessmentTurnover.Heading"),
            infoData: async ({ oData, context }) => {
                const { Amount, Status } = await getVatAssessmentTurnoverData();
                const limits = await getVatAssessmentTurnoverLimits(oData);
                const nextLimit = limits?.find(limit => limit > Amount) ?? limits?.[limits.length - 1];
                return [
                    {
                        value: NumberType.format(Math.floor(Amount), { maximumFractionDigits: 0 }),
                        unit: "Kč",
                        severity: getVatAssessmentTurnoverSeverity(Status?.Code),
                        label: nextLimit ? i18next.t("Home:VatAssessmentTurnover.From", { number: Currency.format(nextLimit, { currency: getCompanyCurrency(context) }) }) : null
                    }
                ];
            },
            isVisible: not(agendaIsVatRegistered),
            CPs: UserPermissionCodes.VatStatement,
            size: { w: 2, h: 1 }
        },
        ValueAddedTaxDates: {
            type: DashboardTileType.Info,
            title: i18next.t("Home:ValueAddedTaxDates.Heading"),
            infoData: async ({ oData, context }) => {
                const [lastSubmission,] = await Promise.all([
                    getLastSubmissionByType(oData, ElectronicSubmissionTypeCode.VatStatement),
                    i18next.loadNamespaces("ElectronicSubmission")
                ]);
                const lastSubmissionStartDate = lastSubmission?.DatePeriodStart;
                const {
                    period,
                    submissionDate
                } = await getNextElectronicSubmissionDate(lastSubmissionStartDate, oData, context);
                const now = getUtcDayjs();
                const lastSubmissionPeriodName = lastSubmissionStartDate ? getVatSubmissionPeriodName(lastSubmissionStartDate, getVatSubmissionFrequencyFromPeriod(lastSubmission)) : EMPTY_DASH;
                return [
                    {
                        value: period ? capitalize(getVatSubmissionPeriodName(period.from, getVatSubmissionFrequencyFromPeriod(period))) : EMPTY_DASH,
                        severity: submissionDate && now.isAfter(submissionDate) ? Status.Error : null,
                        label: i18next.t("Home:ValueAddedTaxDates.Last", { date: lastSubmissionPeriodName })
                    }
                ];
            },
            isVisible: agendaIsVatRegistered,
            CPs: UserPermissionCodes.VatStatement,
            size: { w: 2, h: 1 },
            link: ROUTE_ELECTRONIC_SUBMISSION
        },
        UnfinishedDocuments: {
            type: DashboardTileType.Table,
            title: i18next.t("Home:UnfinishedDocuments.Heading"),
            size: { w: 4, h: 3 },
            tableData: async ({ context, oData, permissionContext }) => {
                const sortedDocEntityTypes = getSortedDocumentEntityTypeNamesWithPermission(permissionContext, false);

                const [counts] = await Promise.all([
                    fetchUnfinishedDocumentsCounts(oData, sortedDocEntityTypes),
                    i18next.loadNamespaces(["FiscalYearClose", "Document"])
                ]);
                return getUnfinishedDocumentsTableData(context, sortedDocEntityTypes, counts, {
                    addDocumentIcon: false,
                    hideStatusIcon: true,
                    showEmptyLines: true
                });
            },
            CPs: [UserPermissionCodes.InvoicesIssued, UserPermissionCodes.InvoicesReceived, UserPermissionCodes.InternalDocuments]
        },
        BankTransactions: getPaymentDocumentTileDef(EntitySetName.BankTransactions),
        CashReceipts: getPaymentDocumentTileDef(EntitySetName.CashReceipts),
        BankAccounts: getBankAccountsTileDef(),
        CashBoxes: getCashBoxTileDef(),
        Grants: getGrantTileDef(),
        NotClearedDocuments: getNotClearedDocumentsDef(),
        KeyboardShortcuts: getKeyboardShortcutsTileDef(),
        AgendaWorkOverview: getAgendaWorkOverviewTileDef(false),
        AgendaWorkOverviewCashBasisAccounting: getAgendaWorkOverviewTileDefCashBasisAccounting(),
        // FutureExpensesAndRevenues: getFutureExpensesAndRevenuesDef(),
        // EstimatedAccruals: getEstimatedAccrualsDef(),
        // Inbox: {
        //     ...getInboxTileDef(),
        //     CPs: UserPermissionCodes.InBox
        // },
        DemoTenant: getDemoTenantTileDef(),
        CustomerSupport: getCustomerSupportTileDef(),
        Manuals: getManualsDef(),
        Chats: getChatsTileDef(),
    };

    const optionalTiles: (keyof TTileDefinition)[] = ["FutureExpensesAndRevenues", "EstimatedAccruals", "Inbox", "DemoTenant", "ProfitLoss", "CreditAccountBalances", "LiabilitiesToShareholders"];
    const defaultTiles = Object.keys(tileDefinition).filter(key => !optionalTiles.includes(key));

    return {
        tileDefinition,
        isVisible: ifAll(not(isOnOrganizationLevel), not(isInCustomerPortal)),
        groups: [{
            id: "all",
            title: i18next.t("Home:AgendaTitle"),
            tiles: defaultTiles,
            allTiles: [...defaultTiles, ...optionalTiles]
        }]
    };
}
